import { serialize, type SerializeOptions } from 'cookie'

export const RESTAURANT_COOKIE = process.env.NEXT_PUBLIC_RESTAURANT_COOKIE ?? 'restaurant_id'

const DEFAULT_SESSION_COOKIE = 'tl_session'
const DEFAULT_REFRESH_COOKIE = 'tl_refresh'

const DEFAULT_SESSION_TTL_SECONDS = 60 * 60 * 24 * 7
const DEFAULT_REFRESH_TTL_SECONDS = 60 * 60 * 24 * 30

const COOKIE_DOMAIN = process.env.NEXT_PUBLIC_COOKIE_DOMAIN

function isLocalHttpHost(host?: string | null): boolean {
  const hostname = host?.split(':')[0]?.trim().toLowerCase()
  if (!hostname) return false
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname === '[::1]' ||
    hostname.endsWith('.localhost')
  )
}

function shouldUseSecureCookies(host?: string | null): boolean {
  // `next start` sets NODE_ENV=production, but Secure cookies are dropped by
  // browsers on plain http://localhost and break the post-login redirect.
  if (isLocalHttpHost(host)) return false
  return process.env.NODE_ENV === 'production'
}

function resolveRefreshCookieSecurity(host?: string | null) {
  return shouldUseSecureCookies(host)
}

function resolveRefreshCookieSameSite(host?: string | null): 'lax' | 'strict' | 'none' {
  return resolveRefreshCookieSecurity(host) ? 'none' : 'lax'
}

/**
 * Derives the cookie domain from a host header at runtime.
 * For demo instances (demo-X.trueloyalty.ai), returns .demo-X.trueloyalty.ai
 * For staging (*.staging.trueloyalty.ai), returns .staging.trueloyalty.ai
 * Falls back to NEXT_PUBLIC_COOKIE_DOMAIN if set, or undefined.
 */
export function resolveCookieDomain(host: string | undefined | null): string | undefined {
  if (!host) {
    return COOKIE_DOMAIN || undefined
  }

  // Remove port if present
  const hostname = host.split(':')[0]

  // Match demo-X.trueloyalty.ai pattern
  const demoMatch = hostname.match(/^(demo-\d+\.trueloyalty\.ai)$/)
  if (demoMatch) {
    return `.${demoMatch[1]}`
  }

  // Match api-demo-X.trueloyalty.ai pattern (backend)
  const apiDemoMatch = hostname.match(/^api-(demo-\d+\.trueloyalty\.ai)$/)
  if (apiDemoMatch) {
    return `.${apiDemoMatch[1]}`
  }

  // Match staging.trueloyalty.ai and tenant hosts like glowbal.staging.trueloyalty.ai.
  if (hostname === 'staging.trueloyalty.ai' || hostname.endsWith('.staging.trueloyalty.ai')) {
    return '.staging.trueloyalty.ai'
  }

  // For production or other cases, use the env var
  return COOKIE_DOMAIN || undefined
}

export const SESSION_COOKIE = process.env.NEXT_PUBLIC_SESSION_ID_COOKIE
  ?? process.env.SESSION_ID_COOKIE
  ?? DEFAULT_SESSION_COOKIE

export const REFRESH_COOKIE = process.env.NEXT_PUBLIC_REFRESH_COOKIE_NAME
  ?? process.env.REFRESH_COOKIE_NAME
  ?? DEFAULT_REFRESH_COOKIE

function appendBaseOptions(options: SerializeOptions = {}, host?: string | null): SerializeOptions {
  const base: SerializeOptions = {
    path: '/',
    sameSite: 'lax',
    secure: shouldUseSecureCookies(host),
    maxAge: DEFAULT_SESSION_TTL_SECONDS
  }

  // Use runtime-resolved domain if host is provided, otherwise fall back to build-time domain
  const domain = host ? resolveCookieDomain(host) : COOKIE_DOMAIN
  if (domain) {
    base.domain = domain
  }

  return {
    ...base,
    ...options
  }
}

function appendBaseOptionsWithoutDomain(options: SerializeOptions = {}, host?: string | null): SerializeOptions {
  const base: SerializeOptions = {
    path: '/',
    sameSite: 'lax',
    secure: shouldUseSecureCookies(host),
    maxAge: DEFAULT_SESSION_TTL_SECONDS
  }

  return {
    ...base,
    ...options
  }
}

function appendRefreshOptions(options: SerializeOptions = {}, host?: string | null): SerializeOptions {
  const base: SerializeOptions = {
    path: '/',
    sameSite: resolveRefreshCookieSameSite(host),
    secure: resolveRefreshCookieSecurity(host),
    maxAge: DEFAULT_REFRESH_TTL_SECONDS,
    httpOnly: true
  }

  // Use runtime-resolved domain if host is provided, otherwise fall back to build-time domain
  const domain = host ? resolveCookieDomain(host) : COOKIE_DOMAIN
  if (domain) {
    base.domain = domain
  }

  return {
    ...base,
    ...options
  }
}

function appendRefreshOptionsWithoutDomain(options: SerializeOptions = {}, host?: string | null): SerializeOptions {
  const base: SerializeOptions = {
    path: '/',
    sameSite: resolveRefreshCookieSameSite(host),
    secure: resolveRefreshCookieSecurity(host),
    maxAge: DEFAULT_REFRESH_TTL_SECONDS,
    httpOnly: true
  }

  return {
    ...base,
    ...options
  }
}

export function buildCookieExpiryOptions(
  expiresAt: string | undefined | null,
  options: {
    responseDateHeader?: string | null
    fallbackMaxAgeSeconds?: number
  } = {}
): SerializeOptions {
  const fallbackMaxAgeSeconds = options.fallbackMaxAgeSeconds ?? DEFAULT_SESSION_TTL_SECONDS
  const expiryMs = Date.parse(expiresAt ?? '')
  if (!Number.isFinite(expiryMs)) {
    return { maxAge: fallbackMaxAgeSeconds }
  }

  const responseTimestamp = options.responseDateHeader
    ? Date.parse(options.responseDateHeader)
    : NaN
  const now = Number.isFinite(responseTimestamp) ? responseTimestamp : Date.now()
  const maxAge = Math.max(0, Math.floor((expiryMs - now) / 1000))

  return {
    maxAge,
    expires: new Date(expiryMs)
  }
}

export function buildCookie(name: string, value: string, options: SerializeOptions = {}, host?: string | null): string {
  return serialize(name, value, appendBaseOptions(options, host))
}

export function buildRefreshCookie(value: string, options: SerializeOptions = {}, host?: string | null): string {
  return serialize(REFRESH_COOKIE, value, appendRefreshOptions(options, host))
}

export function buildClearedCookie(name: string, options: SerializeOptions = {}, host?: string | null): string {
  return buildCookie(name, '', {
    ...options,
    maxAge: 0,
    expires: new Date(0)
  }, host)
}

export function clearRefreshCookie(host?: string | null): string {
  return buildRefreshCookie('', {
    maxAge: 0,
    expires: new Date(0)
  }, host)
}

export function buildClearedAuthCookies(host?: string | null): string[] {
  const scopedCookies = [
    buildClearedCookie(RESTAURANT_COOKIE, {}, host),
    buildClearedCookie(SESSION_COOKIE, { httpOnly: true }, host),
    clearRefreshCookie(host)
  ]

  const hostOnlyCookies = [
    serialize(RESTAURANT_COOKIE, '', appendBaseOptionsWithoutDomain({
      maxAge: 0,
      expires: new Date(0)
    }, host)),
    serialize(SESSION_COOKIE, '', appendBaseOptionsWithoutDomain({
      httpOnly: true,
      maxAge: 0,
      expires: new Date(0)
    }, host)),
    serialize(REFRESH_COOKIE, '', appendRefreshOptionsWithoutDomain({
      maxAge: 0,
      expires: new Date(0)
    }, host))
  ]

  return Array.from(new Set([...scopedCookies, ...hostOnlyCookies]))
}

export function isManagedAuthSetCookie(cookieHeader: string): boolean {
  const normalized = cookieHeader.trim().toLowerCase()
  return normalized.startsWith(`${RESTAURANT_COOKIE.toLowerCase()}=`)
    || normalized.startsWith(`${SESSION_COOKIE.toLowerCase()}=`)
    || normalized.startsWith(`${REFRESH_COOKIE.toLowerCase()}=`)
}
